/**
 * CursorActions - Action system for Sketch-like behaviors
 *
 * Handles:
 * - Element selection with visual feedback
 * - Attribute changes (color, text, styling)
 * - Click and hover interactions
 * - Custom action callbacks
 */
export default class CursorActions {
  constructor(cursor) {
    this.cursor = cursor
    this.actionElements = new Map() // Track elements we've acted on
    this.selectedElements = new Set() // Track selected elements
    this.actionCallbacks = new Map() // Custom action callbacks

    // Setup action styles
    this.setupStyles()
  }

  /**
   * Setup dynamic styles for actions
   */
  setupStyles() {
    if (document.getElementById('cursor-actions-styles')) return

    const style = document.createElement('style')
    style.id = 'cursor-actions-styles'
    style.textContent = `
      .cursor-action-selected {
        position: relative;
        outline: 2px solid var(--cursor-select-color) !important;
        outline-offset: 2px !important;
        transition: outline 0.2s ease !important;
      }

      .cursor-action-selected::after {
        content: '';
        position: absolute;
        top: -4px;
        left: -4px;
        right: -4px;
        bottom: -4px;
        background: rgba(139, 92, 246, 0.1);
        border-radius: 4px;
        pointer-events: none;
        z-index: 1000;
      }

      .cursor-action-hover {
        background-color: rgba(139, 92, 246, 0.1) !important;
        transition: background-color 0.2s ease !important;
      }

      .cursor-measurement-line {
        position: absolute;
        background: var(--cursor-select-color);
        pointer-events: none;
        z-index: 1001;
      }

      .cursor-measurement-horizontal {
        height: 1px;
      }

      .cursor-measurement-vertical {
        width: 1px;
      }

      .cursor-measurement-label {
        position: absolute;
        background: var(--cursor-select-color);
        color: white;
        padding: 2px 6px;
        border-radius: 3px;
        font-size: 12px;
        font-family: monospace;
        white-space: nowrap;
        pointer-events: none;
        z-index: 1002;
      }

      .cursor-action-changed {
        animation: cursor-action-pulse 0.3s ease;
      }

      @keyframes cursor-action-pulse {
        0% { transform: scale(1); }
        50% { transform: scale(1.05); }
        100% { transform: scale(1); }
      }

      .cursor-action-highlight {
        background: linear-gradient(45deg,
          rgba(139, 92, 246, 0.2) 25%,
          transparent 25%,
          transparent 75%,
          rgba(139, 92, 246, 0.2) 75%
        ) !important;
        background-size: 8px 8px !important;
        animation: cursor-action-highlight-move 0.5s linear infinite !important;
      }

      @keyframes cursor-action-highlight-move {
        0% { background-position: 0 0; }
        100% { background-position: 8px 8px; }
      }
    `
    document.head.appendChild(style)
  }

  /**
   * Execute an action on an element
   * @param {string} action - Action type
   * @param {HTMLElement} element - Target element
   * @param {Object} instruction - Full instruction object
   * @returns {Promise} - Promise that resolves when action is complete
   */
  async executeAction(action, element, instruction = {}) {
    const actionData = {
      action,
      element,
      instruction,
      position: this.cursor.utils.getElementPosition(element),
      bounds: this.cursor.utils.getElementBounds(element),
      timestamp: Date.now(),
    }

    try {
      switch (action) {
        case 'click':
          await this.executeClick(element, instruction)
          break

        case 'hover':
          await this.executeHover(element, instruction)
          break

        case 'select':
          await this.executeSelect(element, instruction)
          break

        case 'deselect':
          await this.executeDeselect(element, instruction)
          break

        case 'change-attributes':
          await this.executeChangeAttributes(element, instruction)
          break

        case 'highlight':
          await this.executeHighlight(element, instruction)
          break

        case 'duplicate':
          await this.executeDuplicate(element, instruction)
          break

        case 'delete':
          await this.executeDelete(element, instruction)
          break

        case 'custom':
          await this.executeCustom(element, instruction)
          break

        case 'reveal-annotation':
          await this.executeRevealAnnotation(element, instruction)
          break

        case 'open-annotation':
          await this.executeOpenAnnotation(element, instruction)
          break

        case 'close-annotation':
          await this.executeCloseAnnotation(element, instruction)
          break

        default:
          this.cursor.debug('❌ Unknown action:', action)
          throw new Error(`Unknown action: ${action}`)
      }

      // Execute callback if provided
      if (instruction.callback && typeof instruction.callback === 'function') {
        await instruction.callback(actionData)
      }

      // Track action
      this.trackAction(actionData)
    } catch (error) {
      this.cursor.debug('❌ Action failed:', error)
      throw error
    }
  }

  /**
   * Execute click action
   * @param {HTMLElement} element - Target element
   * @param {Object} instruction - Instruction object
   */
  async executeClick(element, instruction) {
    const { event = 'click', duration = 200 } = instruction

    // Add visual feedback
    element.style.transform = 'scale(0.95)'
    element.style.transition = 'transform 0.1s ease'

    await this.cursor.utils.wait(100)

    // Restore
    element.style.transform = 'scale(1)'

    // Trigger actual event
    const clickEvent = new MouseEvent(event, {
      bubbles: true,
      cancelable: true,
      view: window,
    })

    element.dispatchEvent(clickEvent)

    await this.cursor.utils.wait(duration)
  }

  /**
   * Execute hover action
   * @param {HTMLElement} element - Target element
   * @param {Object} instruction - Instruction object
   */
  async executeHover(element, instruction) {
    const { duration = 1000 } = instruction

    // Add hover class
    element.classList.add('cursor-action-hover')

    // Trigger mouse events
    const mouseEnter = new MouseEvent('mouseenter', {
      bubbles: true,
      cancelable: true,
      view: window,
    })

    const mouseOver = new MouseEvent('mouseover', {
      bubbles: true,
      cancelable: true,
      view: window,
    })

    element.dispatchEvent(mouseEnter)
    element.dispatchEvent(mouseOver)

    await this.cursor.utils.wait(duration)

    // Remove hover class
    element.classList.remove('cursor-action-hover')

    // Trigger mouse leave
    const mouseLeave = new MouseEvent('mouseleave', {
      bubbles: true,
      cancelable: true,
      view: window,
    })

    element.dispatchEvent(mouseLeave)
  }

  /**
   * Execute select action
   * @param {HTMLElement} element - Target element
   * @param {Object} instruction - Instruction object
   */
  async executeSelect(element, instruction) {
    const { multiple = false, duration = 300 } = instruction

    // Get color from cursor's color property or use provided color or default
    let selectionColor = this.getColorForCursor(this.cursor.config.color)

    // Use explicit color from instruction if provided
    if (instruction.color) {
      selectionColor = instruction.color
    }

    // Clear previous selections if not multiple
    if (!multiple) {
      this.clearSelections()
    }

    // Add selection visual
    element.classList.add('cursor-action-selected')
    element.style.setProperty('--cursor-select-color', selectionColor)

    // Add color class for better styling
    const colorClass = `cursor-action-selected--${this.cursor.config.color}`
    element.classList.add(colorClass)

    // Track selection with color information
    this.selectedElements.add(element)
    element.setAttribute('data-cursor-selection-color', this.cursor.config.color)

    // Skip adding selection handles for now
    // We can re-enable this in the future if needed
    // this.addSelectionHandles(element, selectionColor)

    await this.cursor.utils.wait(duration)
  }

  /**
   * Get color value for cursor color name
   * @param {string} colorName - Color name (purple, blue, green, etc.)
   * @returns {string} - CSS color value
   */
  getColorForCursor(colorName) {
    // Map of color names to their CSS values
    const colorMap = {
      purple: '#8B5CF6', // Violet-500
      blue: '#3B82F6', // Blue-500
      green: '#10B981', // Emerald-500
      orange: '#F59E0B', // Amber-500
      pink: '#EC4899', // Pink-500
      teal: '#14B8A6', // Teal-500
      red: '#EF4444', // Red-500
      indigo: '#6366F1', // Indigo-500
    }

    // Return the mapped color or default to purple if not found
    return colorMap[colorName] || '#8B5CF6'
  }

  /**
   * Update annotations that might be attached to an element
   * @param {HTMLElement} element - Element that was modified
   */
  updateAnnotationsForElement(element) {
    // Check if annotation manager is available
    if (window.annotationManager && typeof window.annotationManager.updatePositionsForElement === 'function') {
      window.annotationManager.updatePositionsForElement(element)
      return
    }

    // Fallback: Find all annotations that might be related to this element
    // Check if element itself has annotation
    const annotations = document.querySelectorAll('[data-controller*="annotation-enhanced"]')

    annotations.forEach((annotationEl) => {
      // Check if annotation's target is the element or one of its parents
      const targetSelector = annotationEl.getAttribute('data-annotation-enhanced-target-value')
      if (targetSelector) {
        const target = document.querySelector(targetSelector)
        if (target && (target === element || element.contains(target) || target.contains(element))) {
          // Try to get the annotation controller and update its position
          const controller = this.cursor.manager.application.getControllerForElementAndIdentifier(
            annotationEl,
            'annotation-enhanced'
          )
          if (controller && typeof controller.updatePosition === 'function') {
            controller.updatePosition()
          }
        }
      }
    })
  }

  /**
   * Execute deselect action
   * @param {HTMLElement} element - Target element
   * @param {Object} instruction - Instruction object
   */
  async executeDeselect(element, instruction) {
    const { duration = 200 } = instruction

    // Remove selection visual
    element.classList.remove('cursor-action-selected')

    // Remove from tracked selections
    this.selectedElements.delete(element)

    // Remove selection handles
    this.removeSelectionHandles(element)

    await this.cursor.utils.wait(duration)
  }

  /**
   * Execute change attributes action
   * @param {HTMLElement} element - Target element
   * @param {Object} instruction - Instruction object
   */
  async executeChangeAttributes(element, instruction) {
    const { attributes = {}, styles = {}, text = null, html = null, animate = true, duration = 500 } = instruction

    // Temporarily disable position updates for all cursors to prevent jumps
    if (this.cursor.manager) {
      this.cursor.manager.cursors.forEach((cursor) => {
        cursor.positionUpdateEnabled = false
      })
    }

    // Add change animation
    if (animate) {
      element.classList.add('cursor-action-changed')
      setTimeout(() => {
        element.classList.remove('cursor-action-changed')
      }, 300)
    }

    // Store original values for potential undo
    const originalValues = {
      attributes: {},
      styles: {},
      text: element.textContent,
      html: element.innerHTML,
    }

    // Change attributes
    Object.entries(attributes).forEach(([key, value]) => {
      originalValues.attributes[key] = element.getAttribute(key)
      element.setAttribute(key, value)
    })

    // Change styles
    Object.entries(styles).forEach(([key, value]) => {
      originalValues.styles[key] = element.style[key]

      // Create a CSS variable name for this property
      const cssVarName = `--applied-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`

      // Handle !important flag in style values
      if (typeof value === 'string' && value.includes('!important')) {
        // Need to use setProperty for !important flag
        const cleanValue = value.replace(/\s*!important\s*/, '').trim()

        // Set both the direct style and CSS variable
        element.style.setProperty(key, cleanValue, 'important')
        element.style.setProperty(cssVarName, cleanValue)
      } else {
        // Set both the direct style and CSS variable
        element.style[key] = value
        element.style.setProperty(cssVarName, value)
      }
    })

    // Change HTML content (takes precedence over text)
    if (html !== null) {
      element.innerHTML = html
    }
    // Change text content (only if HTML wasn't set)
    else if (text !== null) {
      element.textContent = text
    }

    // Store for potential undo
    this.actionElements.set(element, {
      type: 'change-attributes',
      original: originalValues,
      current: { attributes, styles, text, html },
    })

    // Force a reflow to ensure DOM dimensions are updated
    element.offsetHeight // This forces the browser to recalculate layout

    // Update any annotations immediately after DOM changes
    this.updateAnnotationsForElement(element)

    await this.cursor.utils.wait(duration)

    // Re-enable position updates for all cursors after a delay
    if (this.cursor.manager) {
      setTimeout(() => {
        this.cursor.manager.cursors.forEach((cursor) => {
          cursor.positionUpdateEnabled = true
          // Update positions once after re-enabling
          cursor.updatePosition()
        })
      }, 100)
    }
  }

  /**
   * Execute highlight action
   * @param {HTMLElement} element - Target element
   * @param {Object} instruction - Instruction object
   */
  async executeHighlight(element, instruction) {
    const { duration = 1000 } = instruction

    // Get color from cursor's color or use provided color
    let highlightColor = this.getColorForCursor(this.cursor.config.color)
    if (instruction.color) {
      highlightColor = instruction.color
    }

    // Add highlight animation
    element.classList.add('cursor-action-highlight')
    element.style.setProperty('--highlight-color', highlightColor)

    // Add color class for better styling
    const colorClass = `cursor-action-highlight--${this.cursor.config.color}`
    element.classList.add(colorClass)

    await this.cursor.utils.wait(duration)

    // Remove highlight and color class
    element.classList.remove('cursor-action-highlight')
    element.classList.remove(colorClass)
  }

  /**
   * Execute duplicate action
   * @param {HTMLElement} element - Target element
   * @param {Object} instruction - Instruction object
   */
  async executeDuplicate(element, instruction) {
    const { offset = { x: 20, y: 20 }, duration = 500 } = instruction

    // Clone element
    const clone = element.cloneNode(true)

    // Position clone
    if (element.style.position === 'absolute' || element.style.position === 'relative') {
      const rect = element.getBoundingClientRect()
      clone.style.position = 'absolute'
      clone.style.left = `${rect.left + offset.x}px`
      clone.style.top = `${rect.top + offset.y}px`
    }

    // Add to DOM
    element.parentNode.appendChild(clone)

    // Animate in
    clone.style.opacity = '0'
    clone.style.transform = 'scale(0.8)'
    clone.style.transition = 'opacity 0.3s ease, transform 0.3s ease'

    requestAnimationFrame(() => {
      clone.style.opacity = '1'
      clone.style.transform = 'scale(1)'
    })

    await this.cursor.utils.wait(duration)

    return clone
  }

  /**
   * Execute delete action
   * @param {HTMLElement} element - Target element
   * @param {Object} instruction - Instruction object
   */
  async executeDelete(element, instruction) {
    const { animate = true, duration = 300 } = instruction

    if (animate) {
      element.style.transition = 'opacity 0.3s ease, transform 0.3s ease'
      element.style.opacity = '0'
      element.style.transform = 'scale(0.8)'

      await this.cursor.utils.wait(300)
    }

    // Remove from selections
    this.selectedElements.delete(element)
    this.removeSelectionHandles(element)

    // Remove element
    element.remove()

    await this.cursor.utils.wait(duration)
  }

  /**
   * Execute custom action
   * @param {HTMLElement} element - Target element
   * @param {Object} instruction - Instruction object
   */
  async executeCustom(element, instruction) {
    const { actionId, callback, duration = 0 } = instruction

    if (!actionId) {
      throw new Error('Custom action requires actionId')
    }

    // Execute custom callback
    if (callback && typeof callback === 'function') {
      await callback(element, instruction)
    } else if (this.actionCallbacks.has(actionId)) {
      await this.actionCallbacks.get(actionId)(element, instruction)
    } else {
      throw new Error(`Custom action not found: ${actionId}`)
    }

    if (duration > 0) {
      await this.cursor.utils.wait(duration)
    }
  }

  /**
   * Add selection handles to element
   * @param {HTMLElement} element - Target element
   */
  addSelectionHandles(element) {
    const handleId = `cursor-handles-${Date.now()}`
    const bounds = this.cursor.utils.getElementBounds(element)

    if (!bounds) return

    // Create handles container
    const handlesContainer = document.createElement('div')
    handlesContainer.id = handleId
    handlesContainer.style.cssText = `
      position: absolute;
      left: ${bounds.x - 4}px;
      top: ${bounds.y - 4}px;
      width: ${bounds.width + 8}px;
      height: ${bounds.height + 8}px;
      pointer-events: none;
      z-index: 1000;
    `

    // Create 8 handles (corners + sides)
    const handlePositions = [
      { x: 0, y: 0, cursor: 'nw-resize' },
      { x: 50, y: 0, cursor: 'n-resize' },
      { x: 100, y: 0, cursor: 'ne-resize' },
      { x: 100, y: 50, cursor: 'e-resize' },
      { x: 100, y: 100, cursor: 'se-resize' },
      { x: 50, y: 100, cursor: 's-resize' },
      { x: 0, y: 100, cursor: 'sw-resize' },
      { x: 0, y: 50, cursor: 'w-resize' },
    ]

    handlePositions.forEach((pos) => {
      const handle = document.createElement('div')
      handle.style.cssText = `
        position: absolute;
        left: ${pos.x}%;
        top: ${pos.y}%;
        width: 8px;
        height: 8px;
        background: #8B5CF6;
        border: 2px solid white;
        border-radius: 50%;
        transform: translate(-50%, -50%);
        cursor: ${pos.cursor};
      `
      handlesContainer.appendChild(handle)
    })

    document.body.appendChild(handlesContainer)

    // Store for cleanup
    element.setAttribute('data-cursor-handles', handleId)
  }

  /**
   * Remove selection handles from element
   * @param {HTMLElement} element - Target element
   */
  removeSelectionHandles(element) {
    const handleId = element.getAttribute('data-cursor-handles')
    if (handleId) {
      const handles = document.getElementById(handleId)
      if (handles) {
        handles.remove()
      }
      element.removeAttribute('data-cursor-handles')
    }
  }

  /**
   * Clear all selections
   */
  clearSelections() {
    this.selectedElements.forEach((element) => {
      element.classList.remove('cursor-action-selected')
      this.removeSelectionHandles(element)
    })
    this.selectedElements.clear()
  }

  /**
   * Track action for potential undo/redo
   * @param {Object} actionData - Action data
   */
  trackAction(actionData) {
    // Store action for potential undo
    // This could be extended to support full undo/redo functionality
    // this.cursor.debug('📝 Action tracked:', actionData)
  }

  /**
   * Register custom action callback
   * @param {string} actionId - Action ID
   * @param {Function} callback - Callback function
   */
  registerCustomAction(actionId, callback) {
    this.actionCallbacks.set(actionId, callback)
  }

  /**
   * Execute reveal annotation action
   * @param {HTMLElement} element - Target annotation element
   * @param {Object} instruction - Instruction object
   */
  async executeRevealAnnotation(element, instruction) {
    this.cursor.debug('👁️ Revealing annotation:', element)

    // Find the annotation controller
    const annotationController = this.getAnnotationController(element)
    if (!annotationController) {
      this.cursor.debug('❌ No annotation controller found')
      return
    }

    // Update position before revealing to ensure it's at the correct location
    // This is important if the target element has changed size
    if (annotationController.updatePosition) {
      annotationController.updatePosition()
    }

    // Reveal the annotation with animation
    annotationController.reveal({
      open: instruction.open || false,
    })

    // Wait for reveal animation to complete
    await this.cursor.utils.wait(instruction.delay || 400)
  }

  /**
   * Execute open annotation action
   * @param {HTMLElement} element - Target annotation element
   * @param {Object} instruction - Instruction object
   */
  async executeOpenAnnotation(element, instruction) {
    // Find the annotation controller
    const annotationController = this.getAnnotationController(element)
    if (!annotationController) {
      this.cursor.debug('❌ No annotation controller found')
      return
    }

    // Open the annotation
    annotationController.open()

    // Wait for open animation to complete
    await this.cursor.utils.wait(instruction.delay || 300)
  }

  /**
   * Execute close annotation action
   * @param {HTMLElement} element - Target annotation element
   * @param {Object} instruction - Instruction object
   */
  async executeCloseAnnotation(element, instruction) {
    // Find the annotation controller
    const annotationController = this.getAnnotationController(element)
    if (!annotationController) {
      this.cursor.debug('❌ No annotation controller found')
      return
    }

    // Close the annotation
    annotationController.close()

    // Wait for close animation to complete
    await this.cursor.utils.wait(instruction.delay || 200)
  }

  /**
   * Get annotation controller from element
   * @param {HTMLElement} element - Target element
   * @returns {Object|null} - Annotation controller or null
   */
  getAnnotationController(element) {
    // Use annotation manager if available
    if (window.annotationManager) {
      return window.annotationManager.findByElement(element)
    }

    // Fallback to direct controller lookup
    // Check if element is an annotation
    if (
      element.hasAttribute('data-controller') &&
      element.getAttribute('data-controller').includes('annotation-enhanced')
    ) {
      return this.cursor.application.getControllerForElementAndIdentifier(element, 'annotation-enhanced')
    }

    // Check if element is inside an annotation
    const annotationElement = element.closest('[data-controller*="annotation-enhanced"]')
    if (annotationElement) {
      return this.cursor.application.getControllerForElementAndIdentifier(annotationElement, 'annotation-enhanced')
    }

    // Try to find annotation by ID
    if (element.hasAttribute('data-annotation-id')) {
      const annotationId = element.getAttribute('data-annotation-id')
      const annotationByIdElement = document.querySelector(`[data-annotation-id="${annotationId}"]`)
      if (annotationByIdElement) {
        return this.cursor.application.getControllerForElementAndIdentifier(
          annotationByIdElement,
          'annotation-enhanced'
        )
      }
    }

    return null
  }

  /**
   * Cleanup all action elements
   */
  cleanup() {
    // Clear selections
    this.clearSelections()

    // Clear tracking
    this.actionElements.clear()
    this.selectedElements.clear()
    this.actionCallbacks.clear()
  }
}
